import React from 'react';
import {appText} from '../../../assets/text/appText';
import {eye, eyeOff} from '../../../assets/images';
import './styles.css';

type UIType = 'auth' | 'standart';

interface Props {
  value: string;
  onChange: (value: string, isSecure: boolean) => void;
  onEnterKeyDown?: () => void;
  placeholder: string;
  showValidation?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  uiType?: UIType;
  title?: string;
  fieldError?: boolean;
  isMandatory?: boolean;
}

const PasswordInput = React.memo(
  ({
    value,
    onChange,
    onEnterKeyDown,
    placeholder,
    showValidation,
    onFocus,
    onBlur,
    className,
    uiType,
    title,
    fieldError,
    isMandatory,
  }: Props) => {
    const [showValue, setShowValue] = React.useState(false);

    const onToggleShow = () => {
      setShowValue(!showValue);
    };

    const getRootClass = () => {
      switch (uiType) {
        case 'standart':
          return 'PasswordInputContainerStandart';
        default:
          return 'PasswordInputContainerAuth';
      }
    };

    const renderValidationSection = (title: string, isValid: boolean) => (
      <div className="PasswordInputValidationSectionContainer">
        <div className="PasswordInputValidationRadioOuter">
          {isValid && <div className="PasswordInputValidationRadioInner" />}
        </div>
        <p className="PasswordInputValidationText">{title}</p>
      </div>
    );

    const onPasswordChange = (text: string) => {
      onChange(
        text.trim(),
        isValidLength(text.trim()) &&
          isValidLetterNumber(text.trim()) &&
          isValidUpperLower(text.trim()) &&
          isValidSpecialCharacter(text.trim()),
      );
    };

    const isValidLength = (v: string) => v.length >= 8;

    const isValidLetterNumber = (v: string) =>
      /\d[a-zA-Z]|[a-zA-Z]\d|\d[~,`,!,@,#,$,%,^,&,*,(,),_,-,+,=,{,[,},\],|,\\,:,;,",',<,>,.,?,/]|[~,`,!,@,#,$,%,^,&,*,(,),_,-,+,=,{,[,},\],|,\\,:,;,",',<,>,.,?,/]\d/i.test(
        v,
      );

    const isValidUpperLower = (v: string) => /[A-Z]/.test(v) && /[a-z]/.test(v);

    const isValidSpecialCharacter = (v: string) =>
      /[~,`,!,@,#,$,%,^,&,*,(,),_,-,+,=,{,[,},\],|,\\,:,;,",',<,>,.,?,/]/i.test(
        v,
      );

    return (
      <div className={className ? className : ''}>
        {title && (
          <p className="PasswordInputTitle">
            {title}
            {isMandatory ? <span className="MandatoryIndicator">*</span> : ''}
          </p>
        )}
        <div
          className={`${getRootClass()} ${
            fieldError ? 'PasswordInputError' : ''
          }`}>
          <input
            type={showValue ? 'none' : 'password'}
            value={value}
            onChange={e => onPasswordChange(e.target.value)}
            onKeyDown={e => {
              if (onEnterKeyDown && e.key === 'Enter') {
                onEnterKeyDown();
              }
            }}
            placeholder={placeholder}
            className={'PasswordInputInput'}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          <img
            src={showValue ? eyeOff : eye}
            alt="eye"
            onClick={onToggleShow}
            className="PasswordInputEye"
          />
        </div>

        {showValidation && (
          <div>
            <p className="PasswordInputDetailsTitle">
              {appText.passwordMustContain}
            </p>
            <div className="PasswordInputValidationContainer">
              {renderValidationSection(
                appText.passwordValidationLength,
                isValidLength(value),
              )}
              {renderValidationSection(
                appText.passwordValidationNumber,
                isValidLetterNumber(value),
              )}
              {renderValidationSection(
                appText.passwordValidationCase,
                isValidUpperLower(value),
              )}
              {renderValidationSection(
                appText.passwordValidationCharacter,
                isValidSpecialCharacter(value),
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default PasswordInput;
